import React, { useState } from 'react';
import { Scale, Copy, Trash2 } from 'lucide-react';

const UnitConverterTool = () => {
  const [category, setCategory] = useState('length');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [value, setValue] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const units = {
    length: {
      name: 'Length',
      units: {
        meter: { name: 'Meter (m)', factor: 1 },
        kilometer: { name: 'Kilometer (km)', factor: 1000 },
        centimeter: { name: 'Centimeter (cm)', factor: 0.01 },
        millimeter: { name: 'Millimeter (mm)', factor: 0.001 },
        mile: { name: 'Mile (mi)', factor: 1609.344 },
        yard: { name: 'Yard (yd)', factor: 0.9144 },
        foot: { name: 'Foot (ft)', factor: 0.3048 },
        inch: { name: 'Inch (in)', factor: 0.0254 }
      }
    },
    weight: {
      name: 'Weight',
      units: {
        kilogram: { name: 'Kilogram (kg)', factor: 1 },
        gram: { name: 'Gram (g)', factor: 0.001 },
        milligram: { name: 'Milligram (mg)', factor: 0.000001 },
        pound: { name: 'Pound (lb)', factor: 0.453592 },
        ounce: { name: 'Ounce (oz)', factor: 0.0283495 },
        ton: { name: 'Metric Ton', factor: 1000 }
      }
    },
    area: {
      name: 'Area',
      units: {
        sqmeter: { name: 'Square Meter (m²)', factor: 1 },
        sqkilometer: { name: 'Square Kilometer (km²)', factor: 1000000 },
        sqfoot: { name: 'Square Foot (ft²)', factor: 0.092903 },
        sqyard: { name: 'Square Yard (yd²)', factor: 0.836127 },
        acre: { name: 'Acre', factor: 4046.86 },
        hectare: { name: 'Hectare (ha)', factor: 10000 }
      }
    },
    volume: {
      name: 'Volume',
      units: {
        liter: { name: 'Liter (L)', factor: 1 },
        milliliter: { name: 'Milliliter (mL)', factor: 0.001 },
        gallon: { name: 'Gallon (US)', factor: 3.78541 },
        quart: { name: 'Quart (US)', factor: 0.946353 },
        pint: { name: 'Pint (US)', factor: 0.473176 },
        cup: { name: 'Cup (US)', factor: 0.236588 },
        floz: { name: 'Fluid Ounce (US)', factor: 0.0295735 }
      }
    },
    speed: {
      name: 'Speed',
      units: {
        mps: { name: 'Meters/second (m/s)', factor: 1 },
        kmh: { name: 'Kilometers/hour (km/h)', factor: 0.277778 },
        mph: { name: 'Miles/hour (mph)', factor: 0.44704 },
        knot: { name: 'Knot', factor: 0.514444 },
        fps: { name: 'Feet/second (ft/s)', factor: 0.3048 }
      }
    },
    data: {
      name: 'Digital Storage',
      units: {
        byte: { name: 'Byte (B)', factor: 1 },
        kilobyte: { name: 'Kilobyte (KB)', factor: 1024 },
        megabyte: { name: 'Megabyte (MB)', factor: 1048576 },
        gigabyte: { name: 'Gigabyte (GB)', factor: 1073741824 },
        terabyte: { name: 'Terabyte (TB)', factor: 1099511627776 },
        bit: { name: 'Bit', factor: 0.125 }
      }
    }
  };

  const convert = () => {
    if (!value || !fromUnit || !toUnit) return;

    const num = parseFloat(value);
    if (isNaN(num)) return;

    const categoryUnits = units[category].units;
    const fromFactor = categoryUnits[fromUnit].factor;
    const toFactor = categoryUnits[toUnit].factor;

    // Convert to base unit, then to target unit
    const baseValue = num * fromFactor;
    const converted = baseValue / toFactor;

    setResult({
      value: converted,
      fromUnit: categoryUnits[fromUnit].name,
      toUnit: categoryUnits[toUnit].name
    });
  };

  const copyResult = () => {
    if (result) {
      navigator.clipboard.writeText(result.value.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const swapUnits = () => {
    const temp = fromUnit;
    setFromUnit(toUnit);
    setToUnit(temp);
    if (result) {
      setValue(result.value.toString());
    }
  };

  // Set default units when category changes
  React.useEffect(() => {
    const categoryUnits = Object.keys(units[category].units);
    setFromUnit(categoryUnits[0]);
    setToUnit(categoryUnits[1]);
    setResult(null);
  }, [category]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {Object.entries(units).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setCategory(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              category === key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="grid md:grid-cols-5 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">From</label>
            <select
              value={fromUnit}
              onChange={(e) => setFromUnit(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
            >
              {Object.entries(units[category].units).map(([key, unit]) => (
                <option key={key} value={key}>{unit.name}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-center">
            <button
              onClick={swapUnits}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              title="Swap units"
            >
              ⇄
            </button>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">To</label>
            <select
              value={toUnit}
              onChange={(e) => setToUnit(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
            >
              {Object.entries(units[category].units).map(([key, unit]) => (
                <option key={key} value={key}>{unit.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">Value</label>
          <input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter value to convert"
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500"
            onKeyDown={(e) => e.key === 'Enter' && convert()}
          />
        </div>
      </div>

      <button
        onClick={convert}
        disabled={!value || !fromUnit || !toUnit}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <Scale size={18} />
        Convert
      </button>

      {result && (
        <div className="p-4 bg-gray-800 rounded-lg">
          <div className="text-sm text-gray-400 mb-2">
            {value} {result.fromUnit} =
          </div>
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-green-400">
              {result.value < 0.0001 || result.value > 9999999 
                ? result.value.toExponential(6) 
                : result.value.toFixed(6).replace(/\.?0+$/, '')} {result.toUnit}
            </div>
            <button
              onClick={copyResult}
              className={`p-2 rounded transition-colors ${
                copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <Copy size={18} />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => { setValue(''); setResult(null); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default UnitConverterTool;
